<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
import { ref, computed, onMounted } from 'vue'
import { useContact } from '~/composables/useContact'
import { useSeo } from '~/composables/useSeo'
import { isValidEmail } from '~/utils/emailValidation'

const { contactInfo, submitForm } = useContact()

// Helper for icons and colors (duplicated from AppFooter for self-containment)
const SOLID_ICONS = new Set(['globe', 'link', 'store', 'gamepad'])

const getSocialIcon = (icon: string): string => {
  const name = (icon ?? 'link').startsWith('fa-') ? icon.slice(3) : (icon ?? 'link')
  return (SOLID_ICONS.has(name) ? 'fas' : 'fab') + ' fa-' + name
}

const getPlatformColor = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'discord': return '#5865F2'
    case 'linkedin': return '#0077B5'
    case 'instagram': return '#E4405F'
    case 'x': case 'twitter': return '#FFFFFF'
    case 'facebook': return '#1877F2'
    case 'youtube': return '#FF0000'
    case 'twitch': return '#9146FF'
    case 'tiktok': return '#000000'
    case 'github': return '#333333'
    case 'behance': return '#1769FF'
    case 'dribbble': return '#EA4C89'
    case 'pinterest': return '#BD081C'
    case 'snapchat': return '#FFFC00'
    case 'bluesky': return '#0085FF'
    default: return '#666666'
  }
}

const formData = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
  consent: false,
  subscribeNewsletter: false,
  website: '', // Honeypot
  formLoadedAt: 0 // Time-based validation
})

// Initialize timestamp when form loads
onMounted(() => {
  formData.value.formLoadedAt = Date.now()
})

const isSubmitting = ref(false)
const submitStatus = ref<{ success: boolean; message: string } | null>(null)

const handleSubmit = async () => {
  isSubmitting.value = true
  submitStatus.value = null
  
  try {
    const response: any = await submitForm(formData.value)
    submitStatus.value = response
    if (response.success) {
      if (typeof (window as any).umami !== 'undefined') {
          (window as any).umami.track('Contact - Sent', { subject: formData.value.subject })
      }
      formData.value = { name: '', email: '', subject: '', message: '', consent: false, subscribeNewsletter: false, website: '', formLoadedAt: Date.now() }
    }
  } catch (error) {
    submitStatus.value = { success: false, message: 'Une erreur est survenue. Veuillez réessayer.' }
  } finally {
    isSubmitting.value = false
  }
}

const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'Contact',
  description: 'Une question sur nos formations ? Envie de visiter l\'école ? N\'hésitez pas à nous écrire.'
})

const runtimeConfig = useRuntimeConfig()
const mapImageUrl = computed(() => {
  const cdnUrl = runtimeConfig.public.cdnUrl || ''
  const imagePath = runtimeConfig.public.contactMapImage
  const path = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath
  return `${cdnUrl}/${path}`
})

</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <UiPageHero 
      pageKey="contact"
      badge-text="Contact & Accès"
      title-prefix="PASSEZ"
      title-highlight="NOUS VOIR"
      description="Rien ne remplace une discussion en personne pour découvrir l'esprit et l'ambiance de l'école."
      :tags="['CONTACT', 'INFOS', 'PLAN']"
      height="screen"
      align="left"
      :primary-button="{ text: 'ENVOYER UN MESSAGE', to: '#contact-form', umamiEvent: 'Contact - Hero - Form' }"
      :secondary-button="{ text: 'PLAN D\'ACCÈS', to: '#access-plan', umamiEvent: 'Contact - Hero - Map' }"
    />

    <section class="py-section-py" aria-label="Formulaire de contact et coordonnées">
      <div class="container mx-auto px-container-px">
        <UiSectionHeader 
          align="left"
          titlePrefix="Contactez"
          titleHighlight="OXO"
          subtitle="Une question ? Un projet ? Notre équipe est à votre écoute pour vous accompagner dans votre parcours."
          class="mb-section-header-mb"
        />

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-12 items-stretch mb-16">
          
          <!-- Contact Form (2 cols) -->
          <section id="contact-form" class="lg:col-span-2 bg-oxo-card-bg p-8 md:p-10 rounded-3xl shadow-xl border border-oxo-border flex flex-col h-full">
            <UiSectionHeader 
              title="Envoyez-nous un message"
              align="left"
              :showSeparator="true"
              size="small"
              class="mb-8!"
            />

            <form @submit.prevent="handleSubmit" class="space-y-6 flex-1 flex flex-col">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="contact-name" class="block text-[10px] uppercase tracking-widest font-black text-oxo-red mb-3">Nom complet</label>
                  <input 
                    id="contact-name"
                    v-model="formData.name"
                    type="text" 
                    required
                    class="w-full px-5 py-4 rounded-xl bg-background border border-oxo-border text-foreground focus:outline-hidden focus:border-oxo-red transition-all duration-300 shadow-xs"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label for="contact-email" class="block text-[10px] uppercase tracking-widest font-black text-oxo-red mb-3">Email</label>
                  <input 
                    id="contact-email"
                    v-model="formData.email"
                    type="email" 
                    required
                    class="w-full px-5 py-4 rounded-xl bg-background border border-oxo-border text-foreground focus:outline-hidden focus:border-oxo-red transition-all duration-300 shadow-xs"
                    :class="{ 'border-red-500! text-red-600': formData.email && !isValidEmail(formData.email), 'border-green-500!': formData.email && isValidEmail(formData.email) }"
                    placeholder="john@example.com"
                  />
                  <p v-if="formData.email && !isValidEmail(formData.email)" class="text-[10px] text-red-500 font-bold mt-1 ml-1">
                      Format email invalide
                  </p>
                </div>
              </div>

              <div>
                <label for="contact-subject" class="block text-[10px] uppercase tracking-widest font-black text-oxo-red mb-3">Sujet</label>
                <select 
                  id="contact-subject"
                  v-model="formData.subject"
                  required
                  class="w-full px-5 py-4 rounded-xl bg-background border border-oxo-border text-foreground focus:outline-hidden focus:border-oxo-red transition-all duration-300 appearance-none shadow-xs"
                >
                  <option value="" disabled class="bg-oxo-card-bg text-foreground">Sélectionnez un sujet</option>
                  <option value="admission" class="bg-oxo-card-bg text-foreground">Admission / Candidature</option>
                  <option value="pedagogie" class="bg-oxo-card-bg text-foreground">Programme pédagogique</option>
                  <option value="partenariat" class="bg-oxo-card-bg text-foreground">Partenariat entreprise</option>
                  <option value="autre" class="bg-oxo-card-bg text-foreground">Autre demande</option>
                </select>
              </div>

              <div class="flex-1 min-h-[150px] flex flex-col">
                <label for="contact-message" class="block text-[10px] uppercase tracking-widest font-black text-oxo-red mb-3">Message</label>
                <textarea 
                  id="contact-message"
                  v-model="formData.message"
                  required
                  rows="5"
                  class="w-full px-5 py-4 rounded-xl bg-background border border-oxo-border text-foreground focus:outline-hidden focus:border-oxo-red transition-all duration-300 resize-none shadow-xs flex-1"
                  placeholder="Votre message..."
                ></textarea>
              </div>

              <!-- RGPD Consent -->
              <div class="flex items-start gap-3 p-5 rounded-2xl bg-background border border-oxo-border/30 hover:border-oxo-red/30 transition-all group shadow-xs">
                <div class="flex items-center h-5">
                  <input 
                    id="rgpd-consent"
                    v-model="formData.consent"
                    type="checkbox" 
                    required
                    class="w-5 h-5 rounded-sm border-oxo-border text-oxo-red focus:ring-oxo-red cursor-pointer transition-colors"
                  />
                </div>
                <label for="rgpd-consent" class="text-xs text-foreground-dim leading-relaxed cursor-pointer group-hover:text-foreground transition-colors font-medium">
                  En cochant cette case, j'accepte que les informations saisies soient traitées par OXO dans le cadre de ma demande de contact. Mes données ne seront en aucun cas partagées avec des tiers et resteront uniquement accessibles aux membres de l'administration OXO. 
                  <a href="/politique-de-confidentialite" class="text-oxo-red hover:underline font-bold">Politique de confidentialité</a>.
                </label>
              </div>

              <!-- Newsletter Opt-in -->
              <div class="flex items-start gap-3 p-5 rounded-2xl bg-background border border-oxo-border/30 hover:border-oxo-red/30 transition-all group shadow-xs">
                <div class="flex items-center h-5">
                  <input 
                    id="newsletter-optin"
                    v-model="formData.subscribeNewsletter"
                    type="checkbox" 
                    class="w-5 h-5 rounded-sm border-oxo-border text-oxo-red focus:ring-oxo-red cursor-pointer transition-colors"
                  />
                </div>
                <label for="newsletter-optin" class="text-xs text-foreground-dim leading-relaxed cursor-pointer group-hover:text-foreground transition-colors font-medium">
                  <i class="fas fa-envelope text-oxo-red mr-1" aria-hidden="true"></i>
                  Je souhaite recevoir la newsletter OXO pour rester informé(e) des actualités, événements et opportunités.
                </label>
              </div>

              <!-- Honeypot (hidden from humans, bots will fill this) -->
              <input
                v-model="formData.website"
                type="text"
                name="website"
                autocomplete="off"
                tabindex="-1"
                aria-hidden="true"
                class="absolute left-[-9999px] opacity-0 pointer-events-none h-0 w-0"
              />

              <div v-if="submitStatus" :class="`p-4 rounded-xl text-sm font-bold animate-fade-in ${submitStatus.success ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`">
                {{ submitStatus.message }}
              </div>

              <AppButton 
                type="submit" 
                variant="primary" 
                size="lg"
                class="w-full justify-center mt-4"
                :disabled="isSubmitting || !formData.consent || (!!formData.email && !isValidEmail(formData.email))"
              >
                <span v-if="isSubmitting"><i class="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i> Envoi en cours...</span>
                <span v-else>Envoyer le message</span>
              </AppButton>
            </form>
          </section>

          <!-- Contact Details (1 col) -->
          <section class="bg-oxo-card-bg p-8 md:p-10 rounded-3xl shadow-xl border border-oxo-border flex flex-col h-full">
            <UiSectionHeader 
              title="Infos Rapides"
              align="left"
              :showSeparator="true"
              size="small"
              class="mb-8!"
            />
            
            <div class="flex-1 flex flex-col justify-center gap-12">
              <div class="flex flex-col items-center text-center group">
                <div class="w-16 h-16 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center text-oxo-yellow text-2xl mb-5 group-hover:scale-110 transition-transform duration-500 shadow-xs shadow-oxo-yellow/5">
                  <i class="fas fa-phone" aria-hidden="true"></i>
                </div>
                <h3 class="font-bold mb-2 uppercase tracking-widest text-[10px] text-oxo-red">Téléphone</h3>
                <p class="text-foreground-dim text-lg font-medium">{{ contactInfo.phone }}</p>
              </div>

              <div class="flex flex-col items-center text-center group">
                <div class="w-16 h-16 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center text-blue-500 text-2xl mb-5 group-hover:scale-110 transition-transform duration-500 shadow-xs shadow-blue-500/5">
                  <i class="fas fa-envelope" aria-hidden="true"></i>
                </div>
                <h3 class="font-bold mb-2 uppercase tracking-widest text-[10px] text-oxo-red">Email</h3>
                <p class="text-foreground-dim text-sm font-medium break-all">{{ contactInfo.email }}</p>
              </div>

              <div class="flex flex-col items-center text-center group">
                <div class="w-16 h-16 rounded-2xl bg-oxo-card-bg/20 border border-oxo-border flex items-center justify-center text-purple-500 text-2xl mb-5 group-hover:scale-110 transition-transform duration-500 shadow-xs shadow-purple-500/5">
                  <i class="fas fa-hashtag" aria-hidden="true"></i>
                </div>
                <h3 class="font-bold mb-2 uppercase tracking-widest text-[10px] text-oxo-red">Réseaux</h3>
                <div class="flex gap-6 justify-center">
                  <a v-for="social in contactInfo.socials" :key="social.platform" :href="social.url" target="_blank" rel="noopener noreferrer" :aria-label="`Suivez-nous sur ${social.platform}`" class="text-2xl text-foreground-dim hover:text-oxo-red transition-colors duration-300">
                    <i :style="faIconStyle(getSocialIcon(social.icon))" :class="getSocialIcon(social.icon)" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- Visuel "Venez nous rencontrer" -->
        <div class="mb-16 animate-fade-in-up">
          <AppMeetUs />
        </div>

        <div id="access-plan" class="w-full animate-slide-up">
          <a 
            href="https://www.google.com/maps/place/OXO+L'%C3%A9cole+occitane+du+jeu+vid%C3%A9o/@43.1818324,2.9759965,17z/data=!3m2!4b1!5s0x12b1ac52df740551:0x755103fdcc4d431f!4m6!3m5!1s0x12b1ad10417fa105:0xed32dd73899fbbe6!8m2!3d43.1818285!4d2.9785714!16s%2Fg%2F11rq65zhv0?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D" 
            target="_blank"
            class="block bg-oxo-card-bg rounded-2xl shadow-lg border border-oxo-border overflow-hidden h-[500px] relative group"
            rel="noopener noreferrer"
          >
            <NuxtImg loading="lazy"
              :src="mapImageUrl"
              alt=""
              class="w-full h-full object-cover grayscale invert brightness-50 dark:brightness-[0.35] contrast-125 group-hover:grayscale-0 group-hover:invert-0 group-hover:brightness-100 transition-all duration-700"
              sizes="100vw"
            />
            <div class="absolute inset-0 bg-linear-to-t from-oxo-dark/80 via-transparent to-transparent pointer-events-none"></div>
            
            <!-- Map Label -->
            <div class="absolute bottom-6 left-6 flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-oxo-red flex items-center justify-center text-white shadow-lg animate-pulse">
                <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
              </div>
              <div class="bg-oxo-dark/80 backdrop-blur-md px-4 py-2 rounded-lg border border-white/10">
                <span class="text-white font-bold text-sm block">Ouvrir l'itinéraire</span>
                <span class="text-oxo-red text-[10px] font-mono uppercase font-black">43.181828, 2.978571</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <AppCTA
      title="Rejoignez"
      titleHighlight="L'AVENTURE"
      subtitle="Intégrez une école faite par des passionnés, pour des passionnés."
      buttonText="Candidater maintenant"
    >
      <AppDiscoverLinks />
    </AppCTA>
  </div>
</template>

<style scoped>
</style>
